import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { SuppressionSection } from '@/components/settings/SuppressionSection';
import { McpKeysSection } from '@/components/settings/McpKeysSection';
import { LockBadge, useToast, YenUnitToggle, YenUnit } from '@/components/ui';
import { formatMoney } from '@/lib/currency';
import { api } from '@/lib/api';
import { billingApi } from '@/lib/billing';
import { companiesApi } from '@/lib/companies';
import { gdprApi, DataExport } from '@/lib/gdprApi';
import { useCurrentUser, useLogout } from '@/hooks/useAuth';
import { usePlan, PLAN_AI_CAP_USD, Plan } from '@/hooks/usePlan';

interface Company {
  id: string;
  name: string;
  countryCode: string;
  primaryLocale: string;
  industry: string | null;
  aiMonthlyCapCents: number | null;
  plan: 'free' | 'growth' | 'global' | 'enterprise';
}

// Model catalogue is intentionally NOT surfaced in the UI — we keep
// the vendor agnostic on the customer side. The backend selects
// defaults from LLM_DEFAULT_MODEL + LLM_REASONING_MODEL env vars; the
// AI cap below is the only AI knob the user gets to turn.

export function SettingsPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const logout = useLogout();
  const me = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingBanner, setBillingBanner] = useState<'success' | 'cancelled' | null>(null);

  // Stripe return — banner + refresh queries + strip params.
  useEffect(() => {
    const status = searchParams.get('billing');
    if (status === 'success' || status === 'cancelled') {
      setBillingBanner(status);
      if (status === 'success') {
        qc.invalidateQueries({ queryKey: ['companies'] });
      }
      const next = new URLSearchParams(searchParams);
      next.delete('billing');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // AI spend MTD is intrinsically a small USD number for most workspaces,
  // but JP customers tracking 8-digit annualised spend prefer 千円 /
  // 百万円. The toggle hides itself entirely unless the workspace's
  // reporting currency is JPY (resolved from company.countryCode below).
  const [aiSpendUnit, setAiSpendUnit] = useState<YenUnit>('1');

  const [lastExport, setLastExport] = useState<DataExport | null>(null);
  const [deletePhrase, setDeletePhrase] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Per-user account-delete (distinct from per-workspace).
  const [selfDeleteOpen, setSelfDeleteOpen] = useState(false);
  const [selfDeletePhrase, setSelfDeletePhrase] = useState('');

  const deleteSelf = useMutation({
    mutationFn: async () => {
      const userId = me.data?.id;
      const email = me.data?.email;
      if (!userId || !email) throw new Error('not signed in');
      if (selfDeletePhrase !== email) {
        throw new Error('phrase_mismatch');
      }
      await gdprApi.deleteUser(userId, selfDeletePhrase);
    },
    onSuccess: async () => {
      toast.success(t('gdpr.self_delete_done'));
      await logout();
    },
    onError: (err) => {
      if ((err as Error).message === 'phrase_mismatch') {
        toast.error(t('gdpr.self_delete_phrase_mismatch'));
        return;
      }
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(msg || t('gdpr.self_delete_failed'));
    },
  });

  const companies = useQuery<Company[]>({
    queryKey: ['companies', 'mine'],
    queryFn: async () => (await api.get<Company[]>('/api/companies')).data,
  });
  const company = companies.data?.[0];
  const plan = usePlan();

  const aiUsage = useQuery({
    queryKey: ['ai-usage', wsId],
    queryFn: () => billingApi.aiUsage(wsId),
    enabled: !!wsId,
  });

  const seatUsage = useQuery({
    queryKey: ['seat-usage', company?.id],
    queryFn: () => companiesApi.seatUsage(company!.id),
    enabled: !!company?.id,
  });

  const [capUsd, setCapUsd] = useState<string>('');

  const effectiveCapUsd =
    capUsd !== '' ? capUsd : company?.aiMonthlyCapCents != null ? String(company.aiMonthlyCapCents / 100) : '';

  const saveAi = useMutation({
    mutationFn: async () => {
      if (!company) return;
      // Model selection is server-driven (env vars); the UI only
      // exposes the spend cap so we don't leak the LLM vendor.
      const patch: Record<string, unknown> = {};
      if (effectiveCapUsd === '') {
        patch.aiMonthlyCapCents = null;
      } else {
        const n = Number(effectiveCapUsd);
        if (!Number.isNaN(n) && n >= 0) patch.aiMonthlyCapCents = Math.round(n * 100);
      }
      const r = await api.patch<Company>(`/api/companies/${company.id}/settings`, patch);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });

  const checkout = useMutation({
    mutationFn: async (opts: { plan: 'growth' | 'global' | 'enterprise'; interval: 'monthly' | 'yearly' }) => {
      if (!company) throw new Error('no company');
      return billingApi.checkout({ plan: opts.plan, interval: opts.interval, seats: 5, companyId: company.id });
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
  });

  const portal = useMutation({
    mutationFn: async () => {
      if (!company) throw new Error('no company');
      return billingApi.portal(company.id);
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err) => {
      // Most common case: 503 "no Stripe customer on file" — happens
      // before the first paid checkout. Surface a clear toast instead
      // of swallowing the error silently.
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 503) {
        toast.neutral(t('settings.billing_portal_needs_subscription'));
      } else {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error).message;
        toast.error(msg || t('settings.billing_portal_failed'));
      }
    },
  });

  /* ── GDPR — export + delete ─────────────────────────────── */

  const requestExport = useMutation({
    mutationFn: async () => {
      if (!company) throw new Error('no company');
      return gdprApi.requestCompanyExport(company.id);
    },
    onSuccess: (row) => {
      setLastExport(row);
      if (row.status === 'ready') {
        toast.success(t('gdpr.export_ready'));
      } else if (row.status === 'failed') {
        toast.error(t('gdpr.export_failed'));
      } else {
        toast.neutral(t('gdpr.export_pending'));
      }
    },
    onError: () => toast.error(t('gdpr.export_failed')),
  });

  const deleteCompany = useMutation({
    mutationFn: async () => {
      if (!company) throw new Error('no company');
      await gdprApi.deleteCompany(company.id, deletePhrase);
    },
    onSuccess: async () => {
      toast.success(t('gdpr.delete_done'));
      // Log the user out before navigating — their access token might
      // still point at a now-stale company until refresh.
      await logout();
      navigate('/');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(msg || t('gdpr.delete_failed'));
    },
  });

  const planTier: Plan = plan.plan;
  const planAiCapUsd = PLAN_AI_CAP_USD[planTier];
  const aiSpentUsd = aiUsage.data ? aiUsage.data.monthCents / 100 : 0;
  const aiPct =
    planAiCapUsd > 0 ? Math.min(100, Math.round((aiSpentUsd / planAiCapUsd) * 100)) : 0;

  const seatsUsed = seatUsage.data?.used ?? 0;
  const seatLimit = seatUsage.data?.limit ?? 0;
  const seatsUnlimited = seatLimit < 0;
  const seatPct =
    !seatsUnlimited && seatLimit > 0
      ? Math.min(100, Math.round((seatsUsed / seatLimit) * 100))
      : 0;

  // Per-tier dot colour for the headline pill. Keeping the palette short
  // makes the panel scan-readable at a glance.
  const tierDot: Record<Plan, string> = {
    free: 'bg-ink-400',
    growth: 'bg-accent-500',
    global: 'bg-indigo-500',
    enterprise: 'bg-amber-500',
  };

  return (
    <AppShell titleKey="settings.title">
      <div className="space-y-6 max-w-3xl">
        {/* ── Plan / seats — top of the page so users see their tier
            before anything else. Anchored as #billing so toasts & the
            PlanLockedCard CTA can jump to it. ── */}
        <section
          id="billing"
          className="rounded-2xl bg-white border border-ink-200 p-5 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-ink-900 mb-3">
            {t('settings.plan_section')}
          </h2>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={`inline-block h-2.5 w-2.5 rounded-full ${tierDot[planTier]}`}
                />
                <span className="text-2xl font-bold text-ink-900 capitalize">
                  {t(`plan.tier_${planTier}`)}
                </span>
              </div>
              <p className="mt-1 text-xs text-ink-500">{t('settings.plan_intro')}</p>
            </div>
            <button
              type="button"
              onClick={() => portal.mutate()}
              disabled={portal.isPending || !company}
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-900 font-medium hover:bg-ink-100 disabled:opacity-60"
            >
              {portal.isPending ? t('common.loading') : t('plan.manage_subscription')}
            </button>
          </div>

          {/* Seats row */}
          <div className="mt-5">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="text-ink-500">{t('plan.seats_label')}</span>
              <span className="text-ink-900 font-medium num">
                {seatsUnlimited
                  ? t('plan.seats_unlimited')
                  : t('plan.seats_used_of', { used: seatsUsed, limit: seatLimit })}
              </span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-ink-100 overflow-hidden">
              <div
                className={`h-full transition-all ${
                  seatsUnlimited
                    ? 'bg-emerald-400 w-full'
                    : seatPct >= 90
                      ? 'bg-warn-500'
                      : 'bg-accent-500'
                }`}
                style={{ width: seatsUnlimited ? '100%' : `${seatPct}%` }}
                aria-hidden
              />
            </div>
          </div>

          {/* AI spend row */}
          <div className="mt-4">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="text-ink-500 flex items-center gap-2">
                {t('plan.ai_cap_label')}
                <YenUnitToggle
                  value={aiSpendUnit}
                  onChange={setAiSpendUnit}
                  currency={company?.countryCode === 'JP' ? 'JPY' : 'USD'}
                  className="text-[10px]"
                />
              </span>
              <span className="text-ink-900 font-medium num">
                {(() => {
                  const isJp = company?.countryCode === 'JP';
                  // JP workspaces format with the unit selector;
                  // everyone else gets the plain dollar string.
                  if (!isJp) {
                    return planAiCapUsd < 0
                      ? `$${aiSpentUsd.toFixed(2)} / ${t('plan.ai_cap_unlimited')}`
                      : `$${aiSpentUsd.toFixed(2)} / $${planAiCapUsd.toLocaleString()} (${aiPct}%)`;
                  }
                  const spentLabel = formatMoney(aiSpentUsd, {
                    currency: 'USD',
                    locale: i18n.language,
                    unit: aiSpendUnit,
                    maximumFractionDigits: 2,
                  });
                  if (planAiCapUsd < 0) {
                    return `${spentLabel} / ${t('plan.ai_cap_unlimited')}`;
                  }
                  const capLabel = formatMoney(planAiCapUsd, {
                    currency: 'USD',
                    locale: i18n.language,
                    unit: aiSpendUnit,
                  });
                  return `${spentLabel} / ${capLabel} (${aiPct}%)`;
                })()}
              </span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-ink-100 overflow-hidden">
              <div
                className={`h-full transition-all ${
                  planAiCapUsd < 0
                    ? 'bg-emerald-400 w-full'
                    : aiPct >= 90
                      ? 'bg-warn-500'
                      : 'bg-accent-500'
                }`}
                style={{ width: planAiCapUsd < 0 ? '100%' : `${aiPct}%` }}
                aria-hidden
              />
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-ink-200 p-5">
          <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('settings.ai_section')}</h2>
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-xs text-ink-500">{t('settings.ai_cap_label')}</label>
              <input
                type="number"
                min={0}
                step={1}
                value={effectiveCapUsd}
                onChange={(e) => setCapUsd(e.target.value)}
                placeholder={t('settings.ai_cap_unlimited')}
                className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
              />
            </div>
            {aiUsage.data && (
              <p className="text-xs text-ink-500 num">
                MTD: ${(aiUsage.data.monthCents / 100).toFixed(2)}
                {aiUsage.data.capCents != null && <> / cap ${(aiUsage.data.capCents / 100).toFixed(2)}</>}
              </p>
            )}
            <button
              onClick={() => saveAi.mutate()}
              disabled={!company || saveAi.isPending}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition"
            >
              {saveAi.isPending ? t('common.saving') : t('settings.ai_save_button')}
            </button>
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-ink-200 p-5">
          <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('settings.billing_section')}</h2>
          <p className="text-xs text-ink-500 mb-3">{t('settings.billing_intro')}</p>

          {billingBanner === 'success' && (
            <div role="status" className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800 flex items-center justify-between gap-2">
              <span>{t('settings.billing_success', { defaultValue: 'Subscription updated. It can take a moment for the new plan to reflect — refresh if needed.' })}</span>
              <button type="button" onClick={() => setBillingBanner(null)} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold">×</button>
            </div>
          )}
          {billingBanner === 'cancelled' && (
            <div role="status" className="mb-3 rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[12px] text-ink-700 flex items-center justify-between gap-2">
              <span>{t('settings.billing_cancelled', { defaultValue: 'Checkout cancelled. No changes were made.' })}</span>
              <button type="button" onClick={() => setBillingBanner(null)} className="text-ink-500 hover:text-ink-900 text-xs font-semibold">×</button>
            </div>
          )}

          {/* Existing customers: portal link to manage subscription, change card,
              download invoices. Goes 503 if no Stripe customer is on file yet. */}
          <div className="mb-3">
            <button
              type="button"
              onClick={() => portal.mutate()}
              disabled={portal.isPending}
              className="inline-flex items-center px-3 py-1.5 rounded-md border border-ink-300 text-ink-900 text-sm font-medium hover:bg-ink-100 disabled:opacity-60"
            >
              {portal.isPending ? t('common.loading') : t('settings.billing_open_portal')}
            </button>
            {portal.isError && (
              <p className="text-xs text-ink-500 mt-2">{t('settings.billing_portal_needs_subscription')}</p>
            )}
          </div>

          {/* Plan picker — mirrors the public pricing page so members can
              compare tiers + benefits + price in-app before checking out.
              Current plan is badged; "Switch" calls /billing/checkout
              with the chosen tier + interval. */}
          <PlanPicker
            currentPlan={(company?.plan ?? 'free') as 'free' | 'growth' | 'global' | 'enterprise'}
            companyId={company?.id ?? ''}
            checkoutPending={checkout.isPending}
            checkoutError={checkout.isError}
            onSubscribe={(plan, interval) => checkout.mutate({ plan, interval })}
          />
        </section>

        <section className="rounded-2xl bg-white border border-ink-200 p-5">
          <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('settings.connectors_section')}</h2>
          <p className="text-xs text-ink-500">{t('settings.connectors_intro')}</p>
          <button
            type="button"
            onClick={() => navigate(`/app/workspace/${wsId}/connectors`)}
            className="mt-3 inline-flex items-center px-3 py-1.5 rounded-md border border-ink-300 text-ink-900 text-sm font-medium hover:bg-ink-100"
          >
            {t('app.nav.connectors')}
          </button>
        </section>

        <SuppressionSection wsId={wsId} />

        <McpKeysSection wsId={wsId} />

        {company && (
          <section className="rounded-2xl bg-white border border-ink-200 p-5">
            <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('settings.company_section')}</h2>
            <dl className="text-sm grid grid-cols-2 gap-y-1">
              <dt className="text-ink-500">{t('settings.company_name')}</dt>
              <dd className="text-ink-900">{company.name}</dd>
              <dt className="text-ink-500">{t('settings.company_country')}</dt>
              <dd className="text-ink-900 num">{company.countryCode}</dd>
              <dt className="text-ink-500">{t('settings.company_locale')}</dt>
              <dd className="text-ink-900 num">{company.primaryLocale}</dd>
              <dt className="text-ink-500">{t('settings.company_industry')}</dt>
              <dd className="text-ink-900">{company.industry || '—'}</dd>
            </dl>
          </section>
        )}

        {/* ── GDPR / data-portability — required by /privacy + /dpa.
            Available on Growth and up; Free can request via /privacy@patronez. ── */}
        {company && (
          <section className="rounded-2xl bg-white border border-ink-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold text-ink-900">
                {t('gdpr.export_section')}
              </h2>
              {!plan.meets('growth') && <LockBadge required="growth" />}
            </div>
            <p className="text-xs text-ink-500 mb-3">{t('gdpr.export_intro')}</p>
            <button
              type="button"
              onClick={() => requestExport.mutate()}
              disabled={requestExport.isPending || !plan.meets('growth')}
              aria-disabled={!plan.meets('growth') || undefined}
              title={!plan.meets('growth') ? t('plan.lock_growth_required', { plan: t('plan.tier_growth') }) : undefined}
              className="inline-flex items-center px-3 py-1.5 rounded-md border border-ink-300 text-ink-900 text-sm font-medium hover:bg-ink-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {requestExport.isPending
                ? t('gdpr.export_running')
                : t('gdpr.export_button')}
            </button>
            {lastExport && (
              <div className="mt-3 text-xs text-ink-600">
                <p>
                  {t('gdpr.export_status')}:{' '}
                  <span className="num">{lastExport.status}</span>
                </p>
                {lastExport.status === 'ready' && (
                  <button
                    type="button"
                    onClick={() => gdprApi.download(lastExport.id)}
                    className="mt-2 inline-flex items-center px-3 py-1.5 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600"
                  >
                    {t('gdpr.download_button')}
                  </button>
                )}
                {lastExport.expiresAt && (
                  <p className="text-[11px] text-ink-500 mt-1">
                    {t('gdpr.export_expires_at', {
                      when: new Date(lastExport.expiresAt).toLocaleString(),
                    })}
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {/* ── Notifier channels — Slack/Teams/LINE WORKS/Chatwork need Global. ── */}
        {company && (
          <section className="rounded-2xl bg-white border border-ink-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold text-ink-900">
                {t('settings.notifiers_section')}
              </h2>
              {!plan.meets('global') && <LockBadge required="global" />}
            </div>
            <p className="text-xs text-ink-500 mb-3">{t('settings.notifiers_intro')}</p>
            <ul className="grid grid-cols-2 gap-2">
              {[
                { key: 'email', label: 'Email', gated: false },
                { key: 'in_app', label: 'In-app', gated: false },
                { key: 'slack', label: 'Slack', gated: true },
                { key: 'teams', label: 'Microsoft Teams', gated: true },
                { key: 'line_works', label: 'LINE WORKS', gated: true },
                { key: 'chatwork', label: 'Chatwork', gated: true },
              ].map((ch) => {
                const locked = ch.gated && !plan.meets('global');
                return (
                  <li
                    key={ch.key}
                    className={
                      'flex items-center justify-between rounded-md border px-3 py-2 text-xs ' +
                      (locked
                        ? 'border-ink-200 bg-ink-50 text-ink-500'
                        : 'border-ink-200 bg-white text-ink-900')
                    }
                  >
                    <span className="font-medium">{ch.label}</span>
                    {locked && <LockBadge required="global" />}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* ── SSO (SAML/OIDC) — Enterprise tier only. ── */}
        {company && (
          <section className="rounded-2xl bg-white border border-ink-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold text-ink-900">
                {t('settings.sso_section')}
              </h2>
              {!plan.meets('enterprise') && <LockBadge required="enterprise" />}
            </div>
            <p className="text-xs text-ink-500 mb-3">{t('settings.sso_intro')}</p>
            <button
              type="button"
              disabled={!plan.meets('enterprise')}
              aria-disabled={!plan.meets('enterprise') || undefined}
              title={
                !plan.meets('enterprise')
                  ? t('plan.lock_enterprise_required', { plan: t('plan.tier_enterprise') })
                  : undefined
              }
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-900 font-medium hover:bg-ink-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {t('settings.sso_configure_button')}
            </button>
          </section>
        )}

        {/* ── SCIM tokens — Enterprise tier only. ── */}
        {company && (
          <section className="rounded-2xl bg-white border border-ink-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold text-ink-900">
                {t('settings.scim_section')}
              </h2>
              {!plan.meets('enterprise') && <LockBadge required="enterprise" />}
            </div>
            <p className="text-xs text-ink-500 mb-3">{t('settings.scim_intro')}</p>
            <button
              type="button"
              disabled={!plan.meets('enterprise')}
              aria-disabled={!plan.meets('enterprise') || undefined}
              title={
                !plan.meets('enterprise')
                  ? t('plan.lock_enterprise_required', { plan: t('plan.tier_enterprise') })
                  : undefined
              }
              className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-900 font-medium hover:bg-ink-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {t('settings.scim_create_token_button')}
            </button>
          </section>
        )}

        {/* ── Delete my account — irreversible, per-user ─── */}
        {me.data?.email && (
          <section className="rounded-2xl bg-white border border-warn-500/30 p-5">
            <h2 className="text-sm font-semibold text-warn-600 mb-3">
              {t('gdpr.self_delete_section', 'Delete my account')}
            </h2>
            <p className="text-xs text-ink-600 mb-3">
              {t('gdpr.self_delete_body')}
            </p>
            {!selfDeleteOpen ? (
              <button
                type="button"
                onClick={() => setSelfDeleteOpen(true)}
                className="text-xs px-3 py-1.5 rounded-md border border-warn-500 text-warn-600 hover:bg-warn-500/10"
              >
                {t('gdpr.self_delete_link')}
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-ink-600">
                  {t('gdpr.self_delete_prompt', { email: me.data.email })}
                </p>
                <input
                  type="text"
                  value={selfDeletePhrase}
                  onChange={(e) => setSelfDeletePhrase(e.target.value)}
                  placeholder={me.data.email}
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm num"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelfDeleteOpen(false);
                      setSelfDeletePhrase('');
                    }}
                    disabled={deleteSelf.isPending}
                    className="text-xs px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100 disabled:opacity-60"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={
                      deleteSelf.isPending || selfDeletePhrase !== me.data.email
                    }
                    onClick={() => deleteSelf.mutate()}
                    className="text-xs px-3 py-1.5 rounded-md bg-warn-600 text-white font-medium hover:bg-warn-500 disabled:opacity-60"
                  >
                    {deleteSelf.isPending
                      ? t('gdpr.self_delete_in_progress')
                      : t('gdpr.self_delete_confirm')}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── Delete workspace — irreversible, owner-only ─── */}
        {company && (
          <section className="rounded-2xl bg-white border border-warn-500/30 p-5">
            <h2 className="text-sm font-semibold text-warn-600 mb-3">
              {t('gdpr.delete_section')}
            </h2>
            <p className="text-xs text-ink-600 mb-3">{t('gdpr.delete_intro')}</p>
            {!deleteOpen ? (
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                className="text-xs px-3 py-1.5 rounded-md border border-warn-500 text-warn-600 hover:bg-warn-500/10"
              >
                {t('gdpr.delete_open_button')}
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-ink-600">
                  {t('gdpr.delete_confirm_prompt', { name: company.name })}
                </p>
                <input
                  type="text"
                  value={deletePhrase}
                  onChange={(e) => setDeletePhrase(e.target.value)}
                  placeholder={company.name}
                  className="w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteOpen(false);
                      setDeletePhrase('');
                    }}
                    className="text-xs px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={
                      deleteCompany.isPending || deletePhrase !== company.name
                    }
                    onClick={() => deleteCompany.mutate()}
                    className="text-xs px-3 py-1.5 rounded-md bg-warn-600 text-white font-medium hover:bg-warn-500 disabled:opacity-60"
                  >
                    {deleteCompany.isPending
                      ? t('common.deleting')
                      : t('gdpr.delete_confirm_button')}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}

/**
 * In-app plan picker. Shows Free + 3 paid tiers side-by-side with a
 * monthly/yearly toggle and a current-plan badge on the active tier.
 * Subscribe / Switch calls /api/billing/checkout via the parent's
 * mutation; Free is informational only.
 */
function PlanPicker({
  currentPlan,
  companyId,
  checkoutPending,
  checkoutError,
  onSubscribe,
}: {
  currentPlan: 'free' | 'growth' | 'global' | 'enterprise';
  companyId: string;
  checkoutPending: boolean;
  checkoutError: boolean;
  onSubscribe: (plan: 'growth' | 'global' | 'enterprise', interval: 'monthly' | 'yearly') => void;
}) {
  const { t } = useTranslation();
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');

  // Live Stripe-sourced plan catalogue. Never hardcode prices here —
  // a Stripe Dashboard edit would silently desync the picker from
  // what Checkout actually charges.
  const plansQ = useQuery({ queryKey: ['billing-plans'], queryFn: () => billingApi.plans() });
  const plans = plansQ.data ?? [];
  const priceFor = (code: 'free' | 'growth' | 'global' | 'enterprise'): number => {
    const p = plans.find((x) => x.code === code);
    if (!p) return 0;
    const cents = interval === 'monthly' ? p.monthlyUnitAmountCents : p.yearlyUnitAmountCents;
    return interval === 'yearly' ? Math.round(cents / 12) / 100 : cents / 100;
  };
  const currency = (plans[0]?.currency ?? 'usd').toUpperCase();
  const fmt = (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

  const tiers: Array<{
    code: 'free' | 'growth' | 'global' | 'enterprise';
    name: string;
    perks: string[];
  }> = [
    { code: 'free',       name: t('pricing.free'),       perks: [t('settings.perk_free')] },
    { code: 'growth',     name: t('pricing.growth'),     perks: [t('settings.perk_growth')] },
    { code: 'global',     name: t('pricing.global'),     perks: [t('settings.perk_global')] },
    { code: 'enterprise', name: t('pricing.enterprise'), perks: [t('settings.perk_enterprise')] },
  ];

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3 mt-4">
        <p className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold">
          {t('settings.billing_start_subscription')}
        </p>
        <div className="inline-flex rounded-md border border-ink-200 p-0.5 bg-ink-50 text-[11px] font-semibold">
          {(['monthly', 'yearly'] as const).map((iv) => (
            <button
              key={iv}
              type="button"
              onClick={() => setInterval(iv)}
              className={'px-2.5 py-1 rounded transition ' + (interval === iv ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900')}
            >
              {iv === 'monthly' ? t('settings.bill_monthly') : t('settings.bill_yearly')}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {tiers.map((tier) => {
          const isCurrent = tier.code === currentPlan;
          const price = priceFor(tier.code);
          return (
            <div
              key={tier.code}
              className={
                'rounded-lg border p-3 flex flex-col ' +
                (isCurrent
                  ? 'border-accent-500 bg-accent-50/40 ring-1 ring-accent-500/30'
                  : 'border-ink-200 bg-white')
              }
            >
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold text-ink-900">{tier.name}</h3>
                {isCurrent && <span className="text-[9px] uppercase tracking-wide font-bold text-accent-700">{t('settings.bill_current')}</span>}
              </div>
              <p className="mt-1.5 num text-xl font-bold text-ink-900">
                {plansQ.isLoading ? '—' : fmt(price)}
              </p>
              <p className="text-[10px] text-ink-500">{tier.code === 'free' ? t('settings.bill_free_forever') : (interval === 'monthly' ? t('settings.bill_per_seat_mo') : t('settings.bill_per_seat_yr'))}</p>
              <ul className="mt-2 text-[10.5px] text-ink-600 flex-1">
                {tier.perks.map((p) => (
                  <li key={p}>· {p}</li>
                ))}
              </ul>
              {tier.code !== 'free' && (
                <button
                  type="button"
                  disabled={isCurrent || checkoutPending || !companyId}
                  onClick={() => onSubscribe(tier.code as 'growth' | 'global' | 'enterprise', interval)}
                  className={
                    'mt-3 w-full rounded-md px-2 py-1.5 text-[11px] font-semibold transition ' +
                    (isCurrent
                      ? 'bg-ink-100 text-ink-500 cursor-default'
                      : 'bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-60')
                  }
                >
                  {isCurrent
                    ? t('settings.bill_current_plan')
                    : checkoutPending
                      ? t('settings.bill_opening')
                      : currentPlan === 'free' ? t('settings.bill_subscribe') : t('settings.bill_switch')}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {checkoutError && (
        <p className="text-xs text-red-600 mt-3">{t('settings.billing_not_configured')}</p>
      )}
    </>
  );
}
